/**
 * Attachment List Component
 * Displays email attachments with download functionality
 */

import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Stack,
  Chip
} from '@mui/material';
import {
  AttachFile as AttachFileIcon,
  Download as DownloadIcon,
  Description as FileIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  Archive as ZipIcon
} from '@mui/icons-material';

interface Attachment {
  id: string;
  filename: string;
  original_filename?: string;
  file_size: number;
  mime_type: string;
  created_at?: string;
}

interface AttachmentListProps {
  attachments: Attachment[];
  emailId: string;
}

const AttachmentList: React.FC<AttachmentListProps> = ({ attachments, emailId }) => {
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const getFileIcon = (mimeType: string) => {
    if (mimeType.startsWith('image/')) return <ImageIcon />;
    if (mimeType === 'application/pdf') return <PdfIcon />;
    if (mimeType.includes('zip') || mimeType.includes('rar')) return <ZipIcon />;
    return <FileIcon />;
  };

  const getFileTypeColor = (mimeType: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    if (mimeType.startsWith('image/')) return 'info';
    if (mimeType === 'application/pdf') return 'error';
    if (mimeType.includes('zip') || mimeType.includes('rar')) return 'warning';
    if (mimeType.includes('word') || mimeType.includes('excel')) return 'primary';
    return 'default';
  };

  const handleDownload = async (attachmentId: string, filename: string) => {
    try {
      const response = await fetch(`/api/v3/emails/attachments/${attachmentId}`);
      
      if (!response.ok) {
        throw new Error('Failed to download attachment');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading attachment:', error);
      // Could show a snackbar notification here
    }
  };

  if (!attachments || attachments.length === 0) {
    return null;
  }

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AttachFileIcon sx={{ fontSize: 18 }} />
        Attachments ({attachments.length})
      </Typography>
      
      <Stack spacing={1}>
        {attachments.map((attachment) => (
          <Box 
            key={attachment.id}
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              p: 1.5, 
              bgcolor: 'grey.50',
              borderRadius: 1,
              border: 1,
              borderColor: 'divider',
              '&:hover': {
                bgcolor: 'grey.100',
                borderColor: 'primary.main'
              }
            }}
          >
            <Box sx={{ mr: 2, color: 'text.secondary' }}>
              {getFileIcon(attachment.mime_type)}
            </Box>
            
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  fontWeight: 500,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {attachment.original_filename || attachment.filename}
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  {formatFileSize(attachment.file_size)}
                </Typography>
                
                <Chip
                  label={attachment.mime_type.split('/')[1]?.toUpperCase() || 'FILE'}
                  size="small"
                  color={getFileTypeColor(attachment.mime_type)}
                  sx={{ height: 16, fontSize: '0.65rem' }}
                />
              </Box>
            </Box>
            
            <IconButton 
              size="small" 
              onClick={() => handleDownload(attachment.id, attachment.original_filename || attachment.filename)}
              sx={{ ml: 1 }}
            >
              <DownloadIcon />
            </IconButton>
          </Box>
        ))}
      </Stack>
    </Paper>
  );
};

export default AttachmentList;
